import { INotificationModel, NotificationStatus, TimeHelper } from "@xala/common-services";
import { Button, Col, Divider, Form, IFormValues, Modal, NotificationService, Row, Spin } from "@xala/common-ui";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { checkRequiredFields, NotificationField, processImageUrl } from "./NotificationFormUtils";
import {
  NameField,
  TextContent,
  RecipientTypeField,
  ProviderField,
  PrioritiesField,
  ScheduledOnField,
  ImageUrlField,
  NotificationStatusReadOnlyField,
  CreatorReadOnlyField,
  UrlContentField,
  UrlDescriptionField,
  SelectAssetField,
} from "./fields";
import { FormInstance } from "antd/lib/form";
import "./NotificationForm.scss";
import { TitleField } from "./fields/TitleField";
import { DeepLinkField } from "./fields/DeepLinkField";
import { NotificationProviderSegmentsField } from "./fields/NotificationProviderSegmentsField";

const formLayouts = {
  formItem: {
    labelCol: {
      xs: { span: 8 },
      lg: { span: 6 },
      xl: { span: 4 },
    },
    wrapperCol: {
      xs: { span: 16 },
      lg: { span: 18 },
      xl: { span: 20 },
    },
  },
  modalFormItem: {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
      md: { span: 6 },
      lg: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 18 },
      md: { span: 18 },
      lg: { span: 18 },
    },
  },
  tailFormItem: {
    wrapperCol: {
      style: {
        textAlign: "right",
        marginBottom: 0,
      } as React.CSSProperties,
    },
  },
};

export interface INotificationFormProps {
  isEditMode?: boolean;
  isCreateMode?: boolean;
  notification?: INotificationModel;
  hiddenFields?: NotificationField[];
  hiddeTitles?: boolean;
  onSubmit: (
    data: INotificationModel,
    form: FormInstance<IFormValues>,
    isSended?: boolean,
    recipients?: any[]
  ) => void | Promise<boolean | void>;
  onChangeRecipients?: (data: any) => void;
  recipients?: any[];
  components?: React.ReactNode;
}

const notificationService = NotificationService.getInstance();

export const NotificationForm = ({
  isCreateMode,
  isEditMode,
  onSubmit,
  hiddenFields,
  hiddeTitles = false,
  notification,
  onChangeRecipients,
  recipients,
  components,
}: INotificationFormProps) => {
  const { t } = useTranslation();
    const [form] = Form.useForm();
  const formLayout = isEditMode
    ? formLayouts.formItem
    : formLayouts.modalFormItem;
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [isScheduledAlertVisible, setIsScheduledAlertVisible] = useState(false);

  const isHidden = (key: NotificationField) =>
    !!hiddenFields && hiddenFields.includes(key);

  const onFinishForm = (values: IFormValues) => {
    const processedImageUrl = processImageUrl(values.ImageUrl);

    if (values?.ScheduledOn && TimeHelper.isAfter(values.ScheduledOn)) {
      return setIsScheduledAlertVisible(true);
    }

    if (isEditMode) {
      const data: INotificationModel = {
        ...notification,
        ...values,
        ImageUrl: processedImageUrl,
        Id: notification!.Id,
        Status: NotificationStatus.Draft,
      };
      onSubmit(data, form);
    } else {
      const data: INotificationModel = {
        ...values,
        ImageUrl: processedImageUrl,
        Id: -1,
        RecipientType: "SELECTED_USERS",
      };
      onSubmit(data, form);
    }
  };

  const handleSendNowButton = () => {
    if (!notification?.ScheduledOn) {
      return setIsAlertVisible(true);
    }

    if (TimeHelper.isAfter(notification.ScheduledOn)) {
      return setIsScheduledAlertVisible(true);
    }

    sendNow();
  };

  const sendNow = async () => {
    setIsAlertVisible(false);
    setIsScheduledAlertVisible(false);
    const values = form.getFieldsValue();
    const processedImageUrl = processImageUrl(values.ImageUrl);

    if (notification?.RowVersion && notification?.Id) {
      const submitData = {
        ...notification,
        ...values,
        ImageUrl: processedImageUrl,
        Status: NotificationStatus.Scheduled,
        Recipients: recipients,
      };

      console.log(submitData);

      const isValid = checkRequiredFields(
        submitData,
        (message: string) => notificationService.error({ message })
      );

      if (!isValid) {
        return;
      }

      onSubmit(submitData, form, true);
    }
  }

  if (isEditMode && !notification && !isCreateMode) {
    return <Spin spinning></Spin>;
  }

  return (
    <Form
      {...formLayout}
      name="NotificationForm"
      onFinish={onFinishForm}
      form={form}
    >
      <Modal
        title="Alerta"
        visible={isAlertVisible}
        onCancel={() => setIsAlertVisible(false)}
        onOk={sendNow}
        okText={t("BUTTON_SEND")}
        cancelText={t("BUTTON_CANCEL")}
      >
        Aquesta notificació no és una notificació programada, i serà enviada de manera immediata.
      </Modal>
      <Modal
        title="Alerta"
        visible={isScheduledAlertVisible}
        onOk={() => setIsScheduledAlertVisible(false)}
        okText={t("BUTTON_OK")}
      >
        Error! La notificació no es pot enviar perquè la data i l'hora d'enviament són anteriors al moment actual. Cal establir sempre una data i una hora posteriors al moment de la publicació de la notificació.
      </Modal>
      <Row direction="column" justify="space-between" className="full-height">
        <Col>
          {!hiddeTitles && <Divider orientation="left">{t("NOTIFICATION_GENERAL")}</Divider>}
          <NameField
            isEditMode={isEditMode}
            isHidden={isHidden("Name")}
            notification={notification}
          />
          <ProviderField
            isEditMode={isEditMode}
            isHidden={isHidden("Provider")}
            notification={notification}
          />
          <PrioritiesField
            isEditMode={isEditMode}
            isHidden={isHidden("Priority")}
            notification={notification}
          />
          <ScheduledOnField
            isEditMode={isEditMode}
            isHidden={isHidden("ScheduledOn")}
            notification={notification}
          />

          {!hiddeTitles && 
            <>
              <br/><br/>
              <Divider orientation="left">{t("NOTIFICATION_CONTENT")}</Divider>
            </>
          }
          <SelectAssetField
            isEditMode={isEditMode}
            isHidden={isHidden("SelectAsset")}
            notification={notification}
            form={form}
          />
          <ImageUrlField
            isEditMode={isEditMode}
            isHidden={isHidden("ImageUrl")}
            notification={notification}
          />
          <NotificationStatusReadOnlyField
            isEditMode={isEditMode}
            isHidden={isHidden("Status")}
            notification={notification}
          />
          <CreatorReadOnlyField
            notification={notification}
            isHidden={isHidden("CreatorUserFullName")}
          />
          <UrlContentField
            isHidden={isHidden("UrlContent")}
            notification={notification}
          />
          <UrlDescriptionField
            isHidden={isHidden("UrlDescription")}
            notification={notification}
          />
          <TitleField
            isEditMode={isEditMode}
            isHidden={isHidden("Title")}
            notification={notification}
          />
          <TextContent
            isEditMode={isEditMode}
            isHidden={isHidden("TextContent")}
            notification={notification}
          />
          <DeepLinkField
            isEditMode={isEditMode}
            isHidden={isHidden("DeepLinkUrl")}
            notification={notification}
          />

          {!hiddeTitles &&
            <>
              <br/><br/>
              <Divider orientation="left">{t("NOTIFICATION_RECIPIENTS")}</Divider>
            </>
          }
          <RecipientTypeField
            isEditMode={isEditMode}
            isHidden={isHidden("RecipientType")}
            notification={notification}
            onChangeRecipients={onChangeRecipients}
          />
          <NotificationProviderSegmentsField
            isEditMode={isEditMode}
            isHidden={isHidden("NotificationProviderSegments")}
            notification={notification}
          />
          {components}
          {isEditMode && (
            <div className="form_buttons">
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={
                    notification?.Status === NotificationStatus.Sent ||
                    notification?.Status === NotificationStatus.InProgress ||
                    notification?.Status === NotificationStatus.Scheduled
                  }
                >
                  {t("BUTTON_SAVE")}
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={handleSendNowButton}
                  disabled={
                    notification?.Status === NotificationStatus.Sent ||
                    notification?.Status === NotificationStatus.InProgress ||
                    notification?.Status === NotificationStatus.Scheduled ||
                    isCreateMode
                  }
                >
                  {t("BUTTON_PUBLISH")}
                </Button>
              </Form.Item>
            </div>
          )}
        </Col>
      </Row>
    </Form>
  );
};
